import { Button, FormContainer, FormItem } from "components/ui"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import usePermissions from "utils/hooks/usePermissions"
import { scroller, showMessageError } from "utils/storeHube/helpers"
import { setSelectedTicket } from "../store/dataSlice"
import * as Yup from 'yup'
import { apiGetMentionableUsers, apiTicketReplies } from "services/TicketsService"
import { statusResponse } from "constants/apiResponse.constant"
import { Field, Form, Formik } from "formik"
import { Loading, RichTextEditor } from "components/shared"
import { getDescriptionImages } from "../ticketList/ImageResizer"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { HiOutlinePaperClip, HiOutlinePaperAirplane, HiOutlineX } from 'react-icons/hi'
import { Avatar, Upload } from "components/ui"
import { VscFile } from "react-icons/vsc"

// Tipos permitidos en adjuntos del comentario.
// Se mantienen alineados con FileItemCard.js (visor de adjuntos): cualquier
// extensión fuera de estas listas el visor no la sabe renderizar bien, asi
// que la rechazamos en el cliente con un mensaje claro antes de mandarla
// al backend.
// heic/heif se aceptan en el input pero nunca llegan al backend: el
// componente Upload los convierte a JPEG en el cliente (ver heicFile.js).
const ALLOWED_EXT_IMAGE = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif']
const ALLOWED_EXT_DOC = ['zip', 'pdf', 'docx', 'xlsx']
const ALLOWED_EXT_VIDEO = ['mov', 'mp4', 'avi', 'mkv', 'wmv']
const ALLOWED_EXT_ALL = [...ALLOWED_EXT_IMAGE, ...ALLOWED_EXT_DOC, ...ALLOWED_EXT_VIDEO]
const ACCEPT_ATTR = ALLOWED_EXT_ALL.map((e) => `.${e}`).join(',')

// quill-mention llama a source en cada tecla escrita despues del '@'. Sin
// debounce, escribir un nombre de 8 letras son 8 requests al backend.
const MENTION_DEBOUNCE_MS = 250

const TicketAddComment = ({ ticket, disabled }) => {


    const { id } = ticket

    const { t } = useTranslation()

    const dispatch = useDispatch()

    const { checkPermission } = usePermissions()
    const tickets_can_reply = checkPermission('tickets.can_reply')
    const tickets_can_attachment = checkPermission('tickets.can_attachment')

    const { loadingSelectedTicket } = useSelector(state => state.ticketList.data)


    const [attachedFiles, setAttachedFiles] = useState([])
    // Upload reconstruye su array interno con lodash cloneDeep en cada
    // seleccion nueva, lo que puede corromper los File ya elegidos en
    // rondas anteriores (Blob/File no clona bien). Por eso nos quedamos
    // solo con los archivos nuevos (la cola de updatedFiles) y los
    // agregamos a nuestro propio estado, que nunca pasa por cloneDeep.
    const onFileUpload = (updatedFiles, previousFiles) => {
        const newlyAdded = updatedFiles.slice(previousFiles.length)
        setAttachedFiles(prev => [...prev, ...newlyAdded])
    }

    // Preview blob URLs de los archivos de tipo imagen. Se regeneran cuando
    // cambia el array de archivos y se revocan en el cleanup del effect
    // (corre antes del proximo cambio y en el unmount).
    const previews = useMemo(
        () => attachedFiles.map(f => (f.type?.includes('image') ? URL.createObjectURL(f) : null)),
        [attachedFiles]
    )
    useEffect(() => () => {
        previews.forEach(url => url && URL.revokeObjectURL(url))
    }, [previews])

    const removeFile = (index) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index))
    }

    // Debounce + request en vuelo del autocomplete de menciones. Va en un ref
    // y no en estado porque cambiarlo no tiene que re-renderizar: un render
    // nuevo rehace `modules` y react-quill destruye el editor (ver el comentario
    // del useMemo mas abajo).
    const mentionRequestRef = useRef({ timer: null, controller: null })

    // El cleanup depende de `id` y no de `[]` porque cambiar de hallazgo NO
    // desmonta este componente: la lista de Mis Tareas renderiza el detalle sin
    // `key`, así que sólo cambian las props. Al cambiar `id` se rehace `modules`
    // y react-quill destruye el editor, pero quill-mention no expone destroy y
    // su dropdown cuelga de <body> por el positioningStrategy 'fixed': queda
    // flotando sobre el hallazgo nuevo, con los candidatos del anterior.
    // Verificado en el browser contra staging el 2026-08-10.
    //
    // Sacar el contenedor del DOM es seguro aunque haya otro composer montado:
    // quill-mention vuelve a hacer appendChild del mismo nodo la próxima vez
    // que muestra la lista.
    useEffect(() => () => {
        clearTimeout(mentionRequestRef.current.timer)
        mentionRequestRef.current.controller?.abort()
        document
            .querySelectorAll('body > .ql-mention-list-container')
            .forEach((nodo) => nodo.remove())
    }, [id])

    // Validacion client-side antes de aceptar el archivo en el input.
    // Devuelve string -> el componente Upload muestra ese texto en un toast
    // tipo danger y NO dispara onChange.
    const beforeFileUpload = (newFiles) => {
        for (const f of newFiles) {
            const ext = (f.name.split('.').pop() || '').toLowerCase()
            if (!ALLOWED_EXT_ALL.includes(ext)) {
                return t(
                    'invalid_file_type',
                    'Tipo de archivo no permitido. Adjuntá imágenes (jpg, png, gif, webp, heic), documentos (pdf, docx, xlsx, zip) o videos (mp4, mov, avi, mkv, wmv).'
                )
            }
        }
        return true
    }

    const resetFiles = () => {
        setAttachedFiles([])
    }


    const addComment = (ticket, newComment) => {
        let props = ticket.props
        const comments = [newComment, ...props.ticket_reply]
        const ticketUpdateReply = {
            ...ticket, props: {
                ...props, ticket_reply: comments, ticket_attachment: [...props.ticket_attachment, ...newComment?.ticket_reply_attachment]
            }
        }
        dispatch(setSelectedTicket(ticketUpdateReply))
    }


    const onSubmit = async (formValue, setSubmitting, resetForm) => {

        setSubmitting(true)
        try {

            const { files: embeddedImages, elements } = await getDescriptionImages(formValue?.message)

            const replyFormData = new FormData();
            replyFormData.append("message", elements)
            embeddedImages.map(file => {
                tickets_can_attachment && replyFormData.append("files[]", file);
            })
            tickets_can_attachment && attachedFiles.forEach(file => replyFormData.append("files[]", file))

            const { data, status } = await apiTicketReplies(id, replyFormData)
            if (status === statusResponse.CREATE) {
                // No pre-formatear created_at: el store queda mezclado entre
                // strings formateados ("28-05-2026 12:34") y timestamps ISO,
                // lo que rompe orderBy(created_at) en TicketTimeline y el sort
                // de inlineRepliesByAnswerId en TicketAnswerNew. Los renders
                // siempre llaman dateFormat sobre el value original.
                addComment(ticket, data.data);
                scroller('replies')
                setSubmitting(false)
                resetFiles()
                resetForm()
            }

        } catch (error) {
            showMessageError(error.response.data.message)
            setSubmitting(false)
            resetForm()

        }
    }

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [{ header: [2, 3, 4, false] }],
                    [{ 'size': ['small', false, 'large'] }],
                    ["bold", "italic", "underline"],
                    [{ color: [] }],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
                    [{ 'align': [] }],
                  /*   ["link" ], */
                    ["clean"],
                ],
            },
            clipboard: {
                matchVisual: true,
            },
            // El chip que genera este modulo es:
            //   <span class="mention" data-index data-denotation-char
            //         data-id data-value>﻿<span contenteditable="false">
            //         @Nombre</span>﻿</span>
            // (data-id / data-value, NO data-mention-id). El backend parsea
            // ese data-id, asi que los ids que se manden aca tienen que ser
            // los de users.id.
            mention: {
                allowedChars: /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s]*$/,
                mentionDenotationChars: ['@'],
                // 'fixed' cuelga el dropdown de <body>. Con el default
                // ('normal') lo inserta dentro de .ql-container y lo recorta
                // el overflow-hidden del wrapper del composer: se ven 3 de 5
                // candidatos en el inbox, en Mis Tareas y en TicketDialog.
                positioningStrategy: 'fixed',
                source: (searchTerm, renderList) => {
                    const pending = mentionRequestRef.current

                    clearTimeout(pending.timer)
                    // Aborta la busqueda anterior: sin esto, dos respuestas
                    // pueden volver desordenadas y el dropdown termina
                    // mostrando los candidatos de un termino que ya no esta
                    // escrito.
                    pending.controller?.abort()

                    pending.timer = setTimeout(async () => {
                        const controller = new AbortController()
                        pending.controller = controller

                        try {
                            const { data } = await apiGetMentionableUsers(
                                id,
                                searchTerm,
                                { signal: controller.signal }
                            )

                            renderList(
                                (data?.data ?? []).map(user => ({
                                    id: user.id,
                                    value: [user.name, user.lastname].filter(Boolean).join(' '),
                                })),
                                searchTerm
                            )
                        } catch (error) {
                            // Abortar es el flujo normal mientras se sigue
                            // escribiendo: la request que la reemplazo es la
                            // que va a renderizar. Cualquier otro error deja
                            // el dropdown vacio en vez de colgado con los
                            // resultados viejos.
                            if (!controller.signal.aborted) {
                                renderList([], searchTerm)
                            }
                        }
                    }, MENTION_DEBOUNCE_MS)
                },
            },
        }),
        // El array de dependencias es OBLIGATORIO cuando modules incluye una
        // funcion (source). react-quill tiene `modules` en dirtyProps y lo
        // compara con isEqual: una funcion nueva en cada render hace que
        // destruya y re-instancie el editor en cada update, y en vistas con
        // muchos re-renders (Mis Tareas) el editor queda a medio montar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [id]
    );


    if (tickets_can_reply) {
        return (
            <div className="mb-4">
                <Loading
                    spinnerClass={'hidden'}
                    loading={loadingSelectedTicket}
                >
                    <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm overflow-hidden focus-within:border-indigo-300 dark:focus-within:border-indigo-700 focus-within:ring-2 focus-within:ring-indigo-100 dark:focus-within:ring-indigo-900/30 transition-all">
                            <Formik
                                initialValues={{ message: '' }}
                                validationSchema={
                                    Yup.object().shape({
                                        message: Yup.string().required('Agregue un comentario'),
                                    })
                                }
                                onSubmit={(values, { resetForm, setSubmitting, }) => {
                                    onSubmit(values, setSubmitting, resetForm)
                                }}
                                validateOnChange={false}
                                validateOnBlur={false}
                            >
                                {({ values, touched, errors, resetForm, isSubmitting }) => (
                                    <Form>
                                        <FormContainer>

                                            <FormItem
                                                className="w-full !mb-0 ticket-composer-editor"
                                                invalid={errors.message}
                                                errorMessage={errors.message}
                                            >
                                                <Field
                                                    name="message"
                                                    placeholder={t('writeComment')}
                                                >
                                                    {({ field, form }) => (
                                                        <RichTextEditor
                                                            value={field.value}
                                                            modules={modules}
                                                            onChange={val => {
                                                                form.setFieldValue(field.name, val === '<p><br></p>' ? "" : val)
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                            </FormItem>

                                            {attachedFiles.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5 px-3 pt-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
                                                    {attachedFiles.map((f, index) => (
                                                        <span
                                                            key={f.name + index}
                                                            className="inline-flex items-center gap-1.5 max-w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-1.5 pr-1 py-1 text-[12px] text-gray-700 dark:text-gray-200"
                                                        >
                                                            {previews[index]
                                                                ? <Avatar size={18} src={previews[index]} className="!w-[18px] !h-[18px] shrink-0" />
                                                                : <VscFile className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
                                                            }
                                                            <span className="truncate" title={f.name}>
                                                                {f.name?.length > 22 ? f.name.slice(0, 22) + '…' : f.name}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeFile(index)}
                                                                aria-label={t('remove_file', 'Quitar archivo')}
                                                                className="ml-1 inline-flex items-center justify-center p-2.5 -m-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
                                                            >
                                                                <HiOutlineX className="w-3.5 h-3.5" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="flex justify-between items-center px-3 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
                                                {tickets_can_attachment ? (
                                                    <Upload
                                                        className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                                        onChange={onFileUpload}
                                                        beforeUpload={beforeFileUpload}
                                                        accept={ACCEPT_ATTR}
                                                        showList={false}
                                                        multiple
                                                        fileList={attachedFiles}
                                                    >
                                                        <HiOutlinePaperClip className="w-4 h-4" />
                                                        <span>
                                                            {attachedFiles.length > 0
                                                                ? `${t("can_attachment")} (${attachedFiles.length})`
                                                                : t("can_attachment")}
                                                        </span>
                                                    </Upload>
                                                ) : <span />}

                                                <Button
                                                    size="sm"
                                                    variant="default"
                                                    type="submit"
                                                    disabled={!values?.message?.length || disabled}
                                                    loading={isSubmitting}
                                                    className="!bg-white hover:!bg-gray-50 dark:!bg-gray-800 dark:hover:!bg-gray-700 !border-gray-300 dark:!border-gray-600 !text-gray-700 dark:!text-gray-100 !px-4 inline-flex items-center gap-1.5"
                                                >
                                                    {isSubmitting
                                                        ? t("commenting", "Comentando")
                                                        : (
                                                            <>
                                                                {t("comment", "Comentar")}
                                                                <HiOutlinePaperAirplane className="w-4 h-4 rotate-90" />
                                                            </>
                                                        )
                                                    }
                                                </Button>
                                            </div>
                                        </FormContainer>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </Loading>
            </div>
        )

    }

}
export default TicketAddComment